import { useEffect, useState } from 'react';
import {
  apiClient,
  type Menu,
} from '@kss/api';
import { useAuth } from '@kss/auth';
import { downloadMenuSeedSql } from './menuSeed';

/**
 * IMPORTANT — do NOT use `menuService` from `@kss/api` here.
 *
 * The auto-generated `menuService` wraps `/api/restaurant/Menu` (the
 * restaurant food-menu CRUD), because that's the first swagger path
 * containing the entity name "Menu". The NAVIGATION menu lives at a
 * different URL: `/api/Menu/all` (no group prefix, append `/all`).
 *
 * The codegen has no way to disambiguate these two — both legitimately
 * have the entity name "Menu". Hitting `/restaurant/Menu` returns
 * restaurant food items (usually empty for non-restaurant tenants),
 * which makes the sidebar fall back to the static `sidebarNav.json`
 * with no children — rendering "No submenu for this section" on every
 * group. Fixed 2026-05-17 by calling the navigation endpoint directly
 * via `apiClient`.
 */
const NAV_MENU_URL = '/Menu/all';
const NAV_ROLE_MENU_URL = '/RoleMenu/all';

/**
 * Sidebar node — already nested for direct rendering.
 *
 * `id` mirrors `Menu.menuId` so the consumer can keep a stable selection
 * across reloads. `children` is omitted when the node has no descendants.
 */
export interface SidebarNode {
  id: string;
  name: string;
  icon?: string;
  path?: string;
  children?: SidebarNode[];
}

export interface DynamicMenuResult {
  /** Tree filtered by the current user's role; empty array on first render. */
  nodes: SidebarNode[];
  loading: boolean;
  error: string | null;
}

interface CacheEntry {
  byRole: Map<string, SidebarNode[]>;
  loadedAt: number;
}

let inFlight: Promise<{ menus: Menu[] }> | null = null;
let cache: CacheEntry | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

function pickList(d: unknown): Menu[] {
  if (Array.isArray(d)) return d as Menu[];
  if (d && typeof d === 'object') {
    const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(o.items)) return o.items as Menu[];
    if (Array.isArray(o.data)) return o.data as Menu[];
    if (o.data && Array.isArray((o.data as { items?: unknown[] }).items)) {
      return (o.data as { items: Menu[] }).items;
    }
  }
  return [];
}

async function loadAll(): Promise<{ menus: Menu[] }> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const menus = await apiClient
      .get(NAV_MENU_URL)
      .then((r) => pickList(r.data))
      .catch(() => [] as Menu[]);
    cache = { byRole: new Map(), loadedAt: Date.now() };
    return { menus: menus ?? [] };
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Live `dbo.Menu` shape (verified 2026-05-26 against
 * GET /api/Menu on tenant `bill`):
 *
 *   { id: <uuid string>, code, name, icon, url,
 *     parentMenuId: <uuid string>, sortOrder, isVisible, isActive,
 *     status, indate, updatedAt }
 *
 * Notes:
 *   - There is NO separate `menuId` field today. `id` IS the uuid PK.
 *   - Children reference parents via `parentMenuId` (uuid), not via
 *     numeric `parentId`. Legacy schemas (pre-2026-05-23) used
 *     `parentId: int`; we still handle that for forward-compat if a
 *     tenant DB is on the older shape.
 *
 *   `null` / `0` / `""` for any parent field means "root".
 */
function menuPkInt(m: Menu): string | undefined {
  // Numeric id (legacy schema). Only populated when m.id is a plain
  // number / digit string — UUIDs are caught by menuPkUuid below.
  if (m.id == null) return undefined;
  const s = String(m.id);
  return /^\d+$/.test(s) ? s : undefined;
}

function menuPkUuid(m: Menu): string | undefined {
  // Live schema: uuid lives on `id`. Older schema had a separate
  // `menuId` field — fall back to that for forward-compat.
  const fromId = m.id != null ? String(m.id) : '';
  if (/^[0-9a-fA-F-]{32,36}$/.test(fromId)) return fromId;
  return m.menuId;
}

/** Stable key for use in the byParent map — prefers the uuid; falls back to numeric id. */
function menuKey(m: Menu): string | undefined {
  return menuPkUuid(m) ?? menuPkInt(m);
}

function menuUrl(m: Menu): string | undefined {
  return m.url ?? m.route ?? m.path;
}

function isHidden(m: Menu): boolean {
  if (m.isActive === false) return true;
  // isVisible may arrive as 0/1 (live API) or boolean
  if (m.isVisible === false || m.isVisible === 0) return true;
  if (m.status && String(m.status).toLowerCase() === 'inactive') return true;
  return false;
}

function buildTree(menus: Menu[], allowedIds: Set<string>): SidebarNode[] {
  // Two side-tables so a child's parentMenuId (uuid) or parentId (int)
  // can both resolve to the SAME bucket — the parent's stable key.
  const intToKey = new Map<string, string>(); // parent id (int) → key
  const uuidToKey = new Map<string, string>(); // parent menuId (uuid) → key
  for (const m of menus) {
    const key = menuKey(m);
    if (!key) continue;
    const intPk = menuPkInt(m);
    const uuidPk = menuPkUuid(m);
    if (intPk) intToKey.set(intPk, key);
    if (uuidPk) uuidToKey.set(uuidPk, key);
  }

  const byParent = new Map<string | undefined, Menu[]>();
  for (const m of menus) {
    if (isHidden(m)) continue;
    const key = menuKey(m);
    if (!key) continue;
    if (allowedIds.size > 0 && !allowedIds.has(key)) continue;
    // Resolve this child's parent reference into the same key the
    // parent uses in `byParent`. `0` / `""` / null all mean root.
    const rawIntParent = m.parentId != null ? String(m.parentId) : '';
    const rawUuidParent = m.parentMenuId ?? '';
    const parentKey =
      rawIntParent && rawIntParent !== '0'
        ? intToKey.get(rawIntParent)
        : rawUuidParent
          ? uuidToKey.get(rawUuidParent)
          : undefined;
    const arr = byParent.get(parentKey) ?? [];
    arr.push(m);
    byParent.set(parentKey, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => {
      const sa = a.sortOrder ?? 0;
      const sb = b.sortOrder ?? 0;
      if (sa !== sb) return sa - sb;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }
  function nodesOf(parentKey: string | undefined): SidebarNode[] {
    const arr = byParent.get(parentKey);
    if (!arr) return [];
    return arr.map((m) => {
      const key = menuKey(m)!;
      const kids = nodesOf(key);
      const node: SidebarNode = {
        id: key,
        name: m.name,
        icon: m.icon,
        path: menuUrl(m),
      };
      if (kids.length > 0) node.children = kids;
      return node;
    });
  }
  return nodesOf(undefined);
}

/** Reset cache — call after a tenant switch or role change. */
export function refreshDynamicMenu(): void {
  cache = null;
  inFlight = null;
}

/**
 * Loads the tenant's full menu tree and role-menu join rows once per
 * 5 minutes, then trims the tree down to the menus visible to the
 * current user's role. SuperAdmin/Admin always see every menu.
 *
 * If the API fails or the tenant hasn't seeded RoleMenu yet, the hook
 * returns an empty array and the caller (Sidebar) falls back to the
 * static `sidebarNav.json` config.
 */
export function useDynamicMenu(): DynamicMenuResult {
  const { user } = useAuth();
  const role = String(user?.role ?? '').toLowerCase();
  const [nodes, setNodes] = useState<SidebarNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const cached = cache?.byRole.get(role);
    if (cached && cache && Date.now() - cache.loadedAt < CACHE_TTL_MS) {
      setNodes(cached);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { menus } = await loadAll();
        if (cancelled) return;

        // Empty-menu fallback. The backend returned zero rows — emit a
        // SQL seed file in dev so an operator can populate the table.
        if (menus.length === 0) {
          downloadMenuSeedSql();
        }

        // RoleMenu join was removed (not in live API). Show every active
        // menu — the per-role filter would need to use RolePermission
        // (not yet wired) to gate visibility.
        const tree = buildTree(menus, new Set<string>());
        cache?.byRole.set(role, tree);
        setNodes(tree);
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message ?? 'Failed to load menus');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role]);

  return { nodes, loading, error };
}
